import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Search, Download, Eye, Edit, Mail, Phone, 
  MapPin, ShoppingCart, Award, Users, UserPlus,
  Star, Badge, Building2, CreditCard
} from 'lucide-react';

interface Customer {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'customer' | 'admin';
  membership_number?: string;
  dojo_id?: string;
  dojo_name?: string;
  belt_rank?: string;
  is_active: boolean;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  total_orders: number;
  total_spent: number;
  address?: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

interface Dojo {
  id: string;
  name: string;
  contact_person: string;
  email: string;
  phone?: string;
  address: string;
  discount_rate: number;
  is_active: boolean;
  member_count: number;
}

export function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [dojos, setDojos] = useState<Dojo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'customers' | 'dojos'>('customers');

  // Mock data - will be replaced with API calls
  const mockCustomers: Customer[] = [
    {
      id: 'cust-001',
      email: 'john.smith@email.com',
      first_name: 'John',
      last_name: 'Smith',
      phone: '+61 2 9876 5432',
      role: 'customer',
      membership_number: 'KRT-001',
      dojo_id: 'dojo-001',
      dojo_name: 'Sydney Karate Club',
      belt_rank: 'Black Belt (1st Dan)',
      is_active: true,
      email_verified: true,
      created_at: '2024-09-15T10:30:00Z',
      updated_at: '2024-10-14T09:30:00Z',
      last_login: '2024-10-14T08:15:00Z',
      total_orders: 3,
      total_spent: 127.50,
      address: {
        street: '123 Harbour Bridge Road',
        city: 'Sydney',
        state: 'NSW',
        postal_code: '2000',
        country: 'Australia'
      }
    },
    {
      id: 'cust-002',
      email: 'sarah.j@email.com',
      first_name: 'Sarah',
      last_name: 'Johnson',
      phone: '+61 3 9876 5432',
      role: 'customer',
      membership_number: 'MMA-042',
      dojo_id: 'dojo-002',
      dojo_name: 'Melbourne Martial Arts',
      belt_rank: 'Brown Belt (2nd Dan)',
      is_active: true,
      email_verified: true,
      created_at: '2024-08-20T14:20:00Z',
      updated_at: '2024-10-13T16:45:00Z',
      last_login: '2024-10-13T14:20:00Z',
      total_orders: 5,
      total_spent: 234.75,
      address: {
        street: '456 Collins Street',
        city: 'Melbourne',
        state: 'VIC',
        postal_code: '3000',
        country: 'Australia'
      }
    },
    {
      id: 'cust-003',
      email: 'mike.chen@email.com',
      first_name: 'Mike',
      last_name: 'Chen',
      phone: '+61 7 9876 5432',
      role: 'customer',
      membership_number: 'BKD-SENSEI',
      dojo_id: 'dojo-003',
      dojo_name: 'Brisbane Karate Dojo',
      belt_rank: 'Black Belt (3rd Dan) - Sensei',
      is_active: true,
      email_verified: true,
      created_at: '2024-07-10T11:45:00Z',
      updated_at: '2024-10-12T18:30:00Z',
      last_login: '2024-10-12T17:45:00Z',
      total_orders: 8,
      total_spent: 445.25,
      address: {
        street: '789 Queen Street',
        city: 'Brisbane',
        state: 'QLD',
        postal_code: '4000',
        country: 'Australia'
      }
    },
    {
      id: 'cust-004',
      email: 'emma.davis@email.com',
      first_name: 'Emma',
      last_name: 'Davis',
      role: 'customer',
      membership_number: 'AKC-078',
      belt_rank: 'Brown Belt',
      is_active: true,
      email_verified: false,
      created_at: '2024-10-05T16:00:00Z',
      updated_at: '2024-10-10T12:00:00Z',
      total_orders: 1,
      total_spent: 34.00,
      address: {
        street: '321 Rundle Mall',
        city: 'Adelaide',
        state: 'SA',
        postal_code: '5000',
        country: 'Australia'
      }
    }
  ];

  const mockDojos: Dojo[] = [
    {
      id: 'dojo-001',
      name: 'Sydney Karate Club',
      contact_person: 'Sensei Williams',
      email: 'contact@sydneykarate.com',
      phone: '+61 2 9876 5432',
      address: '123 Harbour Bridge Road, Sydney NSW 2000',
      discount_rate: 10,
      is_active: true,
      member_count: 45
    },
    {
      id: 'dojo-002',
      name: 'Melbourne Martial Arts',
      contact_person: 'Master Chen',
      email: 'info@melbournema.com',
      phone: '+61 3 9876 5432',
      address: '456 Collins Street, Melbourne VIC 3000',
      discount_rate: 15,
      is_active: true,
      member_count: 72
    },
    {
      id: 'dojo-003',
      name: 'Brisbane Karate Dojo',
      contact_person: 'Sensei Thompson',
      email: 'hello@brisbanekarate.com',
      phone: '+61 7 9876 5432',
      address: '789 Queen Street, Brisbane QLD 4000',
      discount_rate: 12,
      is_active: true,
      member_count: 38
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setCustomers(mockCustomers);
      setDojos(mockDojos);
      setLoading(false);
    }, 500);
  }, []);

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.dojo_name && customer.dojo_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.membership_number && customer.membership_number.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesRole = filterRole === 'all' || customer.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const customerStats = {
    total: customers.length,
    active: customers.filter(c => c.is_active).length,
    verified: customers.filter(c => c.email_verified).length,
    totalSpent: customers.reduce((sum, c) => sum + c.total_spent, 0),
    avgSpent: customers.length > 0 ? customers.reduce((sum, c) => sum + c.total_spent, 0) / customers.length : 0
  };

  const dojoStats = {
    total: dojos.length,
    active: dojos.filter(d => d.is_active).length,
    totalMembers: dojos.reduce((sum, d) => sum + d.member_count, 0),
    avgDiscount: dojos.length > 0 ? dojos.reduce((sum, d) => sum + d.discount_rate, 0) / dojos.length : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Management</h1>
          <p className="mt-2 text-gray-600">Manage customer accounts and dojo partnerships</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('customers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'customers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Customers
          </button>
          <button
            onClick={() => setActiveTab('dojos')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dojos'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Dojos
          </button>
        </nav>
      </div>

      {activeTab === 'customers' ? (
        <>
          {/* Customer Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Customers</p>
                    <p className="text-2xl font-bold text-gray-900">{customerStats.total}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-green-600">{customerStats.active}</p>
                  </div>
                  <Star className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Verified</p>
                    <p className="text-2xl font-bold text-purple-600">{customerStats.verified}</p>
                  </div>
                  <Badge className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">${customerStats.totalSpent.toFixed(2)}</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Avg. Spent</p>
                    <p className="text-2xl font-bold text-orange-600">${customerStats.avgSpent.toFixed(2)}</p>
                  </div>
                  <ShoppingCart className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filter */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search customers by name, email, dojo, or membership number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Customers</option>
                    <option value="customer">Regular Customers</option>
                    <option value="admin">Admin Users</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customers Table */}
          <Card>
            <CardHeader>
              <CardTitle>Customer Directory</CardTitle>
              <CardDescription>
                {filteredCustomers.length} of {customers.length} customers shown
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Customer</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Dojo</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Belt Rank</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Orders</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Total Spent</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((customer) => (
                      <tr key={customer.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer.first_name} {customer.last_name}
                            </p>
                            <p className="text-sm text-gray-500">{customer.email}</p>
                            {customer.membership_number && (
                              <p className="text-sm text-gray-500">#{customer.membership_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-gray-900">{customer.dojo_name || 'Independent'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Award className="h-3 w-3 mr-1" />
                            {customer.belt_rank || 'Not specified'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-900">{customer.total_orders}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-900">${customer.total_spent.toFixed(2)}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              customer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {customer.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {customer.email_verified && (
                              <span title="Email Verified">
                                <Badge className="h-4 w-4 text-blue-600" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setShowCustomerDetails(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Dojo Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Dojos</p>
                    <p className="text-2xl font-bold text-gray-900">{dojoStats.total}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Partnerships</p>
                    <p className="text-2xl font-bold text-green-600">{dojoStats.active}</p>
                  </div>
                  <Star className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Members</p>
                    <p className="text-2xl font-bold text-purple-600">{dojoStats.totalMembers}</p>
                  </div>
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Avg. Discount</p>
                    <p className="text-2xl font-bold text-orange-600">{dojoStats.avgDiscount.toFixed(1)}%</p>
                  </div>
                  <CreditCard className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dojos Table */}
          <Card>
            <CardHeader>
              <CardTitle>Dojo Partnerships</CardTitle>
              <CardDescription>
                Manage dojo partnerships and discount agreements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Dojo</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Contact</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Members</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Discount</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dojos.map((dojo) => (
                      <tr key={dojo.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{dojo.name}</p>
                            <p className="text-sm text-gray-500">{dojo.address}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{dojo.contact_person}</p>
                            <p className="text-sm text-gray-500">{dojo.email}</p>
                            {dojo.phone && (
                              <p className="text-sm text-gray-500">{dojo.phone}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Users className="h-3 w-3 mr-1" />
                            {dojo.member_count} members
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {dojo.discount_rate}% off
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            dojo.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {dojo.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end space-x-2">
                            <Button size="sm" variant="outline">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-screen overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </CardTitle>
                  <CardDescription>
                    Customer since {new Date(selectedCustomer.created_at).toLocaleDateString()}
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={() => setShowCustomerDetails(false)}>
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{selectedCustomer.email}</span>
                    </div>
                    {selectedCustomer.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{selectedCustomer.phone}</span>
                      </div>
                    )}
                    {selectedCustomer.address && (
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div className="text-sm">
                          <p>{selectedCustomer.address.street}</p>
                          <p>{selectedCustomer.address.city}, {selectedCustomer.address.state} {selectedCustomer.address.postal_code}</p>
                          <p>{selectedCustomer.address.country}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Martial Arts Info</h3>
                  <div className="space-y-2">
                    {selectedCustomer.dojo_name && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Dojo</p>
                        <p className="text-sm">{selectedCustomer.dojo_name}</p>
                      </div>
                    )}
                    {selectedCustomer.membership_number && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Membership #</p>
                        <p className="text-sm font-mono">{selectedCustomer.membership_number}</p>
                      </div>
                    )}
                    {selectedCustomer.belt_rank && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Belt Rank</p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Award className="h-3 w-3 mr-1" />
                          {selectedCustomer.belt_rank}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Account Statistics</h3>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Orders</p>
                      <p className="text-2xl font-bold text-blue-600">{selectedCustomer.total_orders}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Spent</p>
                      <p className="text-2xl font-bold text-green-600">${selectedCustomer.total_spent.toFixed(2)}</p>
                    </div>
                    {selectedCustomer.last_login && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Last Login</p>
                        <p className="text-sm">{new Date(selectedCustomer.last_login).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Account Status</h3>
                <div className="flex space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedCustomer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedCustomer.is_active ? 'Active Account' : 'Inactive Account'}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedCustomer.email_verified ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedCustomer.email_verified ? 'Email Verified' : 'Email Pending'}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedCustomer.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedCustomer.role === 'admin' ? 'Admin User' : 'Customer'}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline">Send Email</Button>
                <Button variant="outline">View Orders</Button>
                <Button>Edit Customer</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}